import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Link, NavLink } from 'react-router-dom';
import { ShoppingCart, Package, HelpCircle, Zap } from 'lucide-react';
import catIcon from './icons/cat-icon.jpg';
import dogIcon from './icons/dog-icon.jpg';
import hamsterIcon from './icons/hamster-icon.jpg';
import logo from './icons/logo.svg';
import catVector from './icons/cat.svg';
import AnimatedRoutes from './components/AnimatedRoutes';

const App = () => {
  const [selectedBase, setSelectedBase] = useState(null);
  const [customization, setCustomization] = useState({
    color: '#3b82f6',
    accessories: [],
    name: ''
  });
  const [selectedPet, setSelectedPet] = useState(null);
  const featuredSectionRef = useRef(null);

  const baseModels = [
    {
      id: 1,
      name: "Heroic Knight",
      image: "https://placehold.co/400x500/4f46e5/ffffff?text=Heroic+Knight",
      price: 29.99,
      rating: 4.8,
      description: "A brave warrior ready for adventure"
    },
    {
      id: 2,
      name: "Space Explorer",
      image: "https://placehold.co/400x500/06b6d4/ffffff?text=Space+Explorer",
      price: 34.99,
      rating: 4.9,
      description: "Intergalactic adventurer with cosmic gear"
    },
    {
      id: 3,
      name: "Mystic Wizard",
      image: "https://placehold.co/400x500/7c3aed/ffffff?text=Mystic+Wizard",
      price: 32.99,
      rating: 4.7,
      description: "Master of ancient magical arts"
    },
    {
      id: 4,
      name: "Cyber Ninja",
      image: "https://placehold.co/400x500/10b981/ffffff?text=Cyber+Ninja",
      price: 31.99,
      rating: 4.6,
      description: "High-tech stealth warrior"
    },
    {
      id: 5,
      name: "Pirate Captain",
      image: "https://placehold.co/400x500/f59e0b/ffffff?text=Pirate+Captain",
      price: 28.99,
      rating: 4.5,
      description: "Seafaring legend with treasure"
    },
    {
      id: 6,
      name: "Robot Guardian",
      image: "https://placehold.co/400x500/6b7280/ffffff?text=Robot+Guardian",
      price: 36.99,
      rating: 4.9,
      description: "Advanced mechanical protector"
    }
  ];

  const petCategories = [
    {
      name: "Cats",
      icon: catIcon,
      description: "Feline friends with customizable fur and accessories",
      colors: ["#ff6b6b", "#4ecdc4", "#45b7d1", "#96ceb4", "#feca57"]
    },
    {
      name: "Dogs",
      icon: dogIcon,
      description: "Loyal companions with breed-specific features",
      colors: ["#ff9ff3", "#54a0ff", "#5f27cd", "#00d2d3", "#ff9f43"]
    },
    {
      name: "Hamsters",
      icon: hamsterIcon,
      description: "Tiny adventurers with cute accessories",
      colors: ["#c8d6e5", "#00d9d6", "#f79f1f", "#a55eea", "#ed4c67"]
    }
  ];

  const accessories = [
    { id: 'hat', name: 'Cool Hat', icon: '🧢' },
    { id: 'glasses', name: 'Sunglasses', icon: '🕶️' },
    { id: 'cape', name: 'Hero Cape', icon: '🦸' },
    { id: 'backpack', name: 'Adventure Pack', icon: '🎒' },
    { id: 'sword', name: 'Magic Sword', icon: '⚔️' },
    { id: 'shield', name: 'Protective Shield', icon: '🛡️' }
  ];

  const handleCustomization = (key, value) => {
    setCustomization(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleAccessoryToggle = (accessoryId) => {
    setCustomization(prev => ({
      ...prev,
      accessories: prev.accessories.includes(accessoryId)
        ? prev.accessories.filter(id => id !== accessoryId)
        : [...prev.accessories, accessoryId]
    }));
  };

  const addToCart = () => {
    if (!selectedBase) return;
    alert(`Added ${selectedBase.name} to cart!`);
    setSelectedBase(null);
  };

  const closeModal = () => {
    setSelectedBase(null);
  };

  // Add this useEffect to handle body scroll
  useEffect(() => {
    if (selectedBase) {
      // Prevent scrolling on the body when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to ensure scroll is re-enabled when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedBase]);

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex flex-col">
        <header className="bg-white shadow-sm relative z-10 flex-shrink-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4 relative">
              {/* Position wrapper with max-width to contain the navigation elements */}
              <div className="absolute inset-0">
                <div className="flex justify-between items-center h-full max-w-5xl mx-auto px-8">
                  {/* Navigation links */}
                  <nav className="hidden md:flex space-x-6">
                    <NavLink 
                      to="/" 
                      className={({ isActive }) => 
                        `text-gray-600 hover:text-purple-600 transition-colors relative pb-1 ${
                          isActive ? 'text-purple-600 after:w-full' : 'after:w-0'
                        } after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-0.5 after:bg-purple-600 after:transition-all after:duration-300`
                      }
                    >
                      Home
                    </NavLink>
                    <NavLink 
                      to="/about"
                      className={({ isActive }) => 
                        `text-gray-600 hover:text-purple-600 transition-colors relative pb-1 ${
                          isActive ? 'text-purple-600 after:w-full' : 'after:w-0'
                        } after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-0.5 after:bg-purple-600 after:transition-all after:duration-300`
                      }
                    >
                      Sobre
                    </NavLink>
                    <NavLink 
                      to="/contact"
                      className={({ isActive }) => 
                        `text-gray-600 hover:text-purple-600 transition-colors relative pb-1 ${
                          isActive ? 'text-purple-600 after:w-full' : 'after:w-0'
                        } after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-0.5 after:bg-purple-600 after:transition-all after:duration-300`
                      }
                    >
                      Contato
                    </NavLink>
                  </nav>

                  {/* Icons on the right */}
                  <div className="flex items-center space-x-4">
                    <button className="p-2 rounded-full hover:bg-gray-100 transition-colors relative">
                      <HelpCircle className="h-6 w-6 text-gray-600" />
                    </button>
                    <button className="p-2 rounded-full hover:bg-gray-100 transition-colors relative">
                      <Package className="h-6 w-6 text-gray-600" />
                    </button>
                    <button className="p-2 rounded-full hover:bg-gray-100 transition-colors relative">
                      <ShoppingCart className="h-6 w-6 text-gray-600" />
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">0</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Centered logo */}
              <div className="flex items-center justify-center w-full">
                <Link to="/" className="flex items-center space-x-3">
                  <img 
                    src={logo} 
                    alt="CustomToys Logo" 
                    className="w-auto"
                    style={{ height: '40px' }}
                  />
                  <img 
                    src={catVector} 
                    alt="Cat Icon" 
                    className="w-auto"
                    style={{ height: '35px' }}
                  />
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="flex-1 flex flex-col">
          <AnimatedRoutes
            baseModels={baseModels}
            petCategories={petCategories}
            accessories={accessories}
            selectedBase={selectedBase}
            setSelectedBase={setSelectedBase}
            selectedPet={selectedPet}
            setSelectedPet={setSelectedPet}
            featuredSectionRef={featuredSectionRef}
            customization={customization}
            setCustomization={setCustomization}
            handleCustomization={handleCustomization}
            handleAccessoryToggle={handleAccessoryToggle}
            addToCart={addToCart}
            closeModal={closeModal}
          />
        </div>

        <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8 flex-shrink-0 mt-auto">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Zap className="h-8 w-8 text-purple-400" />
                  <h3 className="text-2xl font-bold">CustomToys</h3>
                </div>
                <p className="text-gray-400">
                  Creating magical action figures that bring your imagination to life.
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Products</h4>
                <ul className="space-y-2 text-gray-400">
                  <li><button className="hover:text-white transition-colors text-left">Action Figures</button></li>
                  <li><button className="hover:text-white transition-colors text-left">Pet Figures</button></li>
                  <li><button className="hover:text-white transition-colors text-left">Accessories</button></li>
                  <li><button className="hover:text-white transition-colors text-left">Custom Orders</button></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Support</h4>
                <ul className="space-y-2 text-gray-400">
                  <li><button className="hover:text-white transition-colors text-left">Help Center</button></li>
                  <li><button className="hover:text-white transition-colors text-left">Shipping Info</button></li>
                  <li><button className="hover:text-white transition-colors text-left">Returns</button></li>
                  <li><button className="hover:text-white transition-colors text-left">Contact Us</button></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Connect</h4>
                <ul className="space-y-2 text-gray-400">
                  <li><button className="hover:text-white transition-colors text-left">Instagram</button></li>
                  <li><button className="hover:text-white transition-colors text-left">Facebook</button></li>
                  <li><button className="hover:text-white transition-colors text-left">Twitter</button></li>
                  <li><button className="hover:text-white transition-colors text-left">Newsletter</button></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; 2024 CustomToys. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>

      <style>
        {`
          @keyframes starBurst {
            0% {
              transform: translate(-50%, -50%) scale(0);
              opacity: 1;
            }
            100% {
              transform: translate(-50%, -50%) scale(1.5);
              opacity: 0;
            }
          }
        `}
      </style>
    </Router>
  );
};

export default App;